import React, { useCallback, useEffect, useState, useRef, Fragment, Component } from 'react';
import { TypeIcon, MailIcon, PhoneIcon, HashIcon, AlignLeftIcon, ChevronDownIcon, CircleIcon, CheckSquareIcon, SquareCheckIcon, CalendarIcon, UploadIcon, LinkIcon, EyeOffIcon, HeadingIcon, FileTextIcon, MinusIcon, MoveVerticalIcon, ImageIcon, SendIcon, Trash2Icon, CopyIcon, GripVerticalIcon, UndoIcon, RedoIcon, PaletteIcon, ChevronRightIcon, PlusIcon, XIcon, WandSparklesIcon, Loader2Icon, SwatchBookIcon } from 'lucide-react';
import { FormBlock, FormTheme, FormBlockType, DEFAULT_FORM_THEME, FORM_PALETTE, FORM_STYLE_PRESETS, StylePreset, PaletteItem, createFormBlock, labelToName } from './types';
import { generateFormHtml } from './formHtmlGenerator';
import { callGPTAPI } from '../../utils/api';
const ICONS: Record<string, React.ElementType> = {
  Type: TypeIcon,
  Mail: MailIcon,
  Phone: PhoneIcon,
  Hash: HashIcon,
  AlignLeft: AlignLeftIcon,
  ChevronDown: ChevronDownIcon,
  Circle: CircleIcon,
  CheckSquare: CheckSquareIcon,
  SquareCheck: SquareCheckIcon,
  Calendar: CalendarIcon,
  Upload: UploadIcon,
  Link: LinkIcon,
  EyeOff: EyeOffIcon,
  Heading: HeadingIcon,
  FileText: FileTextIcon,
  Minus: MinusIcon,
  MoveVertical: MoveVerticalIcon,
  Image: ImageIcon,
  Send: SendIcon
};
const BEAUTIFY_INSTRUCTIONS = `You are an expert CSS designer. You receive a signup form's HTML and current CSS. The user will give you a specific instruction about what to change.

RULES:
1. Return ONLY a CSS code block — do NOT change the HTML at all.
2. Keep all existing class names (.fb-form, .fb-field, .fb-label, .fb-input, .fb-button, etc.)
3. CRITICAL: You must ONLY change the specific things the user asked for. Leave ALL other styles exactly as they are in the current CSS.
4. If the user says "make the button red", ONLY change the button color. Do NOT touch fonts, backgrounds, spacing, shadows, or anything else.
5. If the user says "change the font", ONLY change font-related properties. Do NOT touch colors, spacing, shadows, or anything else.
6. Start with the EXACT current CSS and make MINIMAL targeted changes based on the user's instruction.
7. Do NOT add any explanation — ONLY the CSS code block:
\`\`\`css
(your modified CSS here)
\`\`\`
8. The CSS should be self-contained and work with the provided HTML structure.
9. If no specific instruction is given, make subtle improvements while keeping the overall look intact — do NOT redesign from scratch.`;
function parseCssFromResponse(response: string): string {
  const cssMatch = response.match(/```css\s*\n([\s\S]*?)```/i);
  if (cssMatch) return cssMatch[1].trim();
  const genericMatch = response.match(/```\s*\n([\s\S]*?)```/i);
  if (genericMatch) return genericMatch[1].trim();
  return response.trim();
}
interface FormBuilderProps {
  initialBlocks?: FormBlock[];
  initialTheme?: FormTheme;
  onSchemaChange: (blocks: FormBlock[], theme: FormTheme) => void;
  onHtmlChange: (html: string, css: string) => void;
}
/** Group consecutive half-width blocks into rows for canvas display */
interface CanvasRow {
  type: 'full' | 'half-row';
  blocks: {
    block: FormBlock;
    flatIndex: number;
  }[];
}
function groupBlocksIntoRows(blocks: FormBlock[]): CanvasRow[] {
  const rows: CanvasRow[] = [];
  let i = 0;
  while (i < blocks.length) {
    const block = blocks[i];
    if (block.width === 'half') {
      const rowBlocks: {
        block: FormBlock;
        flatIndex: number;
      }[] = [{
        block,
        flatIndex: i
      }];
      // Check if next block is also half
      if (i + 1 < blocks.length && blocks[i + 1].width === 'half') {
        rowBlocks.push({
          block: blocks[i + 1],
          flatIndex: i + 1
        });
        i += 2;
      } else {
        i += 1;
      }
      rows.push({
        type: 'half-row',
        blocks: rowBlocks
      });
    } else {
      rows.push({
        type: 'full',
        blocks: [{
          block,
          flatIndex: i
        }]
      });
      i += 1;
    }
  }
  return rows;
}
export function FormBuilder({
  initialBlocks,
  initialTheme,
  onSchemaChange,
  onHtmlChange
}: FormBuilderProps) {
  const [blocks, setBlocks] = useState<FormBlock[]>(initialBlocks || []);
  const [theme, setTheme] = useState<FormTheme>(initialTheme || DEFAULT_FORM_THEME);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);
  const [history, setHistory] = useState<FormBlock[][]>([initialBlocks || []]);
  const [historyIdx, setHistoryIdx] = useState(0);
  const [collapsedCats, setCollapsedCats] = useState<Set<string>>(new Set());
  const [isBeautifying, setIsBeautifying] = useState(false);
  const [beautifyPrompt, setBeautifyPrompt] = useState('');
  const [showBeautifyInput, setShowBeautifyInput] = useState(false);
  // Track CSS before beautify so undo can revert it
  const [preBeautifyCss, setPreBeautifyCss] = useState<string | null>(null);
  const dragItemRef = useRef<{
    type: 'palette' | 'canvas';
    blockType?: FormBlockType;
    index?: number;
  } | null>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const selectedBlock = blocks.find(b => b.id === selectedId) || null;
  const pushHistory = useCallback((newBlocks: FormBlock[]) => {
    setHistory(prev => {
      const trimmed = prev.slice(0, historyIdx + 1);
      return [...trimmed, newBlocks].slice(-50);
    });
    setHistoryIdx(prev => Math.min(prev + 1, 49));
  }, [historyIdx]);
  const updateBlocks = useCallback((newBlocks: FormBlock[]) => {
    setBlocks(newBlocks);
    pushHistory(newBlocks);
    const {
      html,
      css
    } = generateFormHtml(newBlocks, theme);
    onSchemaChange(newBlocks, theme);
    onHtmlChange(html, css);
  }, [theme, pushHistory, onSchemaChange, onHtmlChange]);
  useEffect(() => {
    const {
      html,
      css
    } = generateFormHtml(blocks, theme);
    onSchemaChange(blocks, theme);
    onHtmlChange(html, css);
  }, [theme]);
  const undo = useCallback(() => {
    // If we have a beautify CSS to revert, do that first
    if (preBeautifyCss !== null) {
      const {
        html
      } = generateFormHtml(blocks, theme);
      onHtmlChange(html, preBeautifyCss);
      setPreBeautifyCss(null);
      return;
    }
    if (historyIdx > 0) {
      const newIdx = historyIdx - 1;
      setHistoryIdx(newIdx);
      const prev = history[newIdx];
      setBlocks(prev);
      const {
        html,
        css
      } = generateFormHtml(prev, theme);
      onSchemaChange(prev, theme);
      onHtmlChange(html, css);
    }
  }, [historyIdx, history, theme, onSchemaChange, onHtmlChange, preBeautifyCss, blocks]);
  const redo = useCallback(() => {
    if (historyIdx < history.length - 1) {
      const newIdx = historyIdx + 1;
      setHistoryIdx(newIdx);
      const next = history[newIdx];
      setBlocks(next);
      const {
        html,
        css
      } = generateFormHtml(next, theme);
      onSchemaChange(next, theme);
      onHtmlChange(html, css);
    }
  }, [historyIdx, history, theme, onSchemaChange, onHtmlChange]);
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
        e.preventDefault();
        if (e.shiftKey) redo();else undo();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [undo, redo]);
  // AI Beautify handler
  const handleBeautify = async (styleDirection?: string) => {
    if (blocks.length === 0) return;
    setIsBeautifying(true);
    try {
      const {
        html,
        css
      } = generateFormHtml(blocks, theme);
      // Save current CSS so undo can revert
      setPreBeautifyCss(css);
      const prompt = `Here is my form HTML:\n\`\`\`html\n${html}\n\`\`\`\n\nHere is the current CSS:\n\`\`\`css\n${css}\n\`\`\`\n\n${styleDirection ? `User instruction: ${styleDirection}` : 'Make subtle improvements to polish the form. Keep the overall look intact.'}`;
      const result = await callGPTAPI(BEAUTIFY_INSTRUCTIONS, prompt);
      const newCss = parseCssFromResponse(result);
      if (newCss) {
        onHtmlChange(html, newCss);
      }
    } catch (err) {
      console.error('Beautify failed:', err);
      setPreBeautifyCss(null); // Clear on failure
    } finally {
      setIsBeautifying(false);
      setShowBeautifyInput(false);
      setBeautifyPrompt('');
    }
  };
  // Drag handlers
  const handlePaletteDragStart = (type: FormBlockType) => (e: React.DragEvent) => {
    dragItemRef.current = {
      type: 'palette',
      blockType: type
    };
    e.dataTransfer.effectAllowed = 'copy';
  };
  const handleCanvasDragStart = (index: number) => (e: React.DragEvent) => {
    dragItemRef.current = {
      type: 'canvas',
      index
    };
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleDragOver = (index: number) => (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = dragItemRef.current?.type === 'palette' ? 'copy' : 'move';
    setDragOverIdx(index);
  };
  const handleDragLeave = () => setDragOverIdx(null);
  const handleBlockDragOver = (index: number) => (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const midY = rect.top + rect.height / 2;
    const dropIdx = e.clientY < midY ? index : index + 1;
    e.dataTransfer.dropEffect = dragItemRef.current?.type === 'palette' ? 'copy' : 'move';
    setDragOverIdx(dropIdx);
  };
  const handleBlockDrop = (index: number) => (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const midY = rect.top + rect.height / 2;
    const dropIdx = e.clientY < midY ? index : index + 1;
    setDragOverIdx(null);
    const drag = dragItemRef.current;
    if (!drag) return;
    if (drag.type === 'palette' && drag.blockType) {
      const newBlock = createFormBlock(drag.blockType);
      const newBlocks = [...blocks];
      newBlocks.splice(dropIdx, 0, newBlock);
      updateBlocks(newBlocks);
      setSelectedId(newBlock.id);
    } else if (drag.type === 'canvas' && drag.index !== undefined) {
      if (drag.index === index) {
        dragItemRef.current = null;
        return;
      }
      const newBlocks = [...blocks];
      const [moved] = newBlocks.splice(drag.index, 1);
      const adjustedIdx = drag.index < dropIdx ? dropIdx - 1 : dropIdx;
      newBlocks.splice(adjustedIdx, 0, moved);
      updateBlocks(newBlocks);
    }
    dragItemRef.current = null;
  };
  const handleDrop = (dropIndex: number) => (e: React.DragEvent) => {
    e.preventDefault();
    setDragOverIdx(null);
    const drag = dragItemRef.current;
    if (!drag) return;
    if (drag.type === 'palette' && drag.blockType) {
      const newBlock = createFormBlock(drag.blockType);
      const newBlocks = [...blocks];
      newBlocks.splice(dropIndex, 0, newBlock);
      updateBlocks(newBlocks);
      setSelectedId(newBlock.id);
    } else if (drag.type === 'canvas' && drag.index !== undefined) {
      const newBlocks = [...blocks];
      const [moved] = newBlocks.splice(drag.index, 1);
      const adjustedIdx = drag.index < dropIndex ? dropIndex - 1 : dropIndex;
      newBlocks.splice(adjustedIdx, 0, moved);
      updateBlocks(newBlocks);
    }
    dragItemRef.current = null;
  };
  const handleCanvasDropEnd = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOverIdx(null);
    const drag = dragItemRef.current;
    if (!drag) return;
    if (drag.type === 'palette' && drag.blockType) {
      const newBlock = createFormBlock(drag.blockType);
      updateBlocks([...blocks, newBlock]);
      setSelectedId(newBlock.id);
    }
    dragItemRef.current = null;
  };
  const deleteBlock = (id: string) => {
    updateBlocks(blocks.filter(b => b.id !== id));
    if (selectedId === id) setSelectedId(null);
  };
  const duplicateBlock = (id: string) => {
    const idx = blocks.findIndex(b => b.id === id);
    if (idx === -1) return;
    const clone = {
      ...blocks[idx],
      id: Math.random().toString(36).substr(2, 9),
      name: blocks[idx].name + '_copy'
    };
    const newBlocks = [...blocks];
    newBlocks.splice(idx + 1, 0, clone);
    updateBlocks(newBlocks);
    setSelectedId(clone.id);
  };
  const updateBlock = (id: string, updates: Partial<FormBlock>) => {
    const newBlocks = blocks.map(b => b.id === id ? {
      ...b,
      ...updates
    } : b);
    setBlocks(newBlocks);
    const {
      html,
      css
    } = generateFormHtml(newBlocks, theme);
    onSchemaChange(newBlocks, theme);
    onHtmlChange(html, css);
  };
  const commitToHistory = () => pushHistory(blocks);
  const categories = [...new Set(FORM_PALETTE.map(p => p.category))];
  const toggleCat = (cat: string) => {
    setCollapsedCats(prev => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);else next.add(cat);
      return next;
    });
  };
  // Group blocks into rows for canvas display
  const canvasRows = groupBlocksIntoRows(blocks);
  // Render a single block card (used for both full and half-width)
  const renderBlockCard = (block: FormBlock, flatIndex: number) => <div key={block.id} draggable onDragStart={handleCanvasDragStart(flatIndex)} onDragOver={handleBlockDragOver(flatIndex)} onDrop={handleBlockDrop(flatIndex)} onClick={e => {
    e.stopPropagation();
    setSelectedId(block.id);
  }} className={`group relative rounded-md p-3 border-2 transition-colors cursor-pointer ${selectedId === block.id ? 'border-blue-500 ring-1 ring-blue-200' : 'border-transparent hover:border-gray-300'}`} data-id="element-365">
      <div className="absolute -left-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-366">
        <GripVerticalIcon className="h-4 w-4 text-gray-400 cursor-grab" data-id="element-367" />
      </div>
      <div className="absolute -right-1 top-0 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity z-10" data-id="element-368">
        <button onClick={e => {
        e.stopPropagation();
        duplicateBlock(block.id);
      }} className="p-1 bg-white border border-gray-200 rounded shadow-sm hover:bg-gray-50" title="Duplicate" data-id="element-369">
          <CopyIcon className="h-3 w-3 text-gray-500" data-id="element-370" />
        </button>
        <button onClick={e => {
        e.stopPropagation();
        deleteBlock(block.id);
      }} className="p-1 bg-white border border-gray-200 rounded shadow-sm hover:bg-red-50" title="Delete" data-id="element-371">
          <Trash2Icon className="h-3 w-3 text-red-500" data-id="element-372" />
        </button>
      </div>
      <CanvasBlockPreview block={block} theme={theme} data-id="element-373" />
    </div>;
  return <div className="flex h-[calc(100vh-220px)] min-h-[500px] border border-gray-200 rounded-lg overflow-hidden bg-white" data-id="element-374">
      {/* Left Panel — Block Palette */}
      <div className="w-60 flex-shrink-0 border-r border-gray-200 bg-gray-50 overflow-y-auto" data-id="element-375">
        <div className="p-3 border-b border-gray-200" data-id="element-376">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-377">
            Blocks
          </h3>
        </div>
        {categories.map(cat => <div key={cat} data-id="element-378">
            <button onClick={() => toggleCat(cat)} className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider hover:bg-gray-100" data-id="element-379">
              {cat}
              <ChevronRightIcon className={`h-3 w-3 transition-transform ${collapsedCats.has(cat) ? '' : 'rotate-90'}`} data-id="element-380" />
            </button>
            {!collapsedCats.has(cat) && <div className="px-2 pb-2 space-y-1" data-id="element-381">
                {FORM_PALETTE.filter(p => p.category === cat).map(item => {
            const Icon = ICONS[item.icon] || TypeIcon;
            return <div key={item.type} draggable onDragStart={handlePaletteDragStart(item.type as FormBlockType)} className="flex items-center gap-2 px-2.5 py-2 rounded-md bg-white border border-gray-200 text-sm text-gray-700 cursor-grab hover:border-blue-300 hover:shadow-sm active:cursor-grabbing select-none" data-id="element-382">
                      <Icon className="h-4 w-4 text-gray-400 flex-shrink-0" data-id="element-383" />
                      <span className="truncate" data-id="element-384">{item.label}</span>
                    </div>;
          })}
              </div>}
          </div>)}
      </div>

      {/* Center — Canvas */}
      <div className="flex-1 overflow-y-auto bg-gray-100 p-6" onClick={() => setSelectedId(null)} data-id="element-385">
        <div className="flex items-center justify-between mb-4" data-id="element-386">
          <div className="flex items-center gap-1" data-id="element-387">
            <button onClick={undo} disabled={historyIdx <= 0} className="p-1.5 rounded hover:bg-gray-200 disabled:opacity-30" title="Undo (Ctrl+Z)" data-id="element-388">
              <UndoIcon className="h-4 w-4 text-gray-500" data-id="element-389" />
            </button>
            <button onClick={redo} disabled={historyIdx >= history.length - 1} className="p-1.5 rounded hover:bg-gray-200 disabled:opacity-30" title="Redo (Ctrl+Shift+Z)" data-id="element-390">
              <RedoIcon className="h-4 w-4 text-gray-500" data-id="element-391" />
            </button>
          </div>

          {/* AI Beautify */}
          <div className="flex items-center gap-2" data-id="element-392">
            {showBeautifyInput && <div className="flex items-center gap-1.5 bg-white border border-purple-200 rounded-lg px-2 py-1 shadow-sm" data-id="element-393">
                <input type="text" value={beautifyPrompt} onChange={e => setBeautifyPrompt(e.target.value)} onKeyDown={e => {
              if (e.key === 'Enter') handleBeautify(beautifyPrompt);
              if (e.key === 'Escape') {
                setShowBeautifyInput(false);
                setBeautifyPrompt('');
              }
            }} placeholder="e.g. dark mode, playful, corporate..." className="text-xs border-none outline-none bg-transparent w-48 placeholder:text-gray-400" autoFocus data-id="element-394" />
                <button onClick={() => handleBeautify(beautifyPrompt)} disabled={isBeautifying} className="p-1 text-purple-600 hover:text-purple-800 disabled:opacity-50" data-id="element-395">
                  <SendIcon className="h-3.5 w-3.5" data-id="element-396" />
                </button>
                <button onClick={() => {
              setShowBeautifyInput(false);
              setBeautifyPrompt('');
            }} className="p-1 text-gray-400 hover:text-gray-600" data-id="element-397">
                  <XIcon className="h-3.5 w-3.5" data-id="element-398" />
                </button>
              </div>}
            <button onClick={() => {
            if (showBeautifyInput) handleBeautify(beautifyPrompt);else if (blocks.length > 0) setShowBeautifyInput(true);
          }} disabled={isBeautifying || blocks.length === 0} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 disabled:opacity-50 transition-colors" title="Use AI to beautify your form's styling" data-id="element-399">
              {isBeautifying ? <Loader2Icon className="h-3.5 w-3.5 animate-spin" data-id="element-400" /> : <WandSparklesIcon className="h-3.5 w-3.5" data-id="element-401" />}
              {isBeautifying ? 'Beautifying...' : '✨ AI Beautify'}
            </button>
            <span className="text-xs text-gray-400" data-id="element-402">
              {blocks.length} block{blocks.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        <div ref={canvasRef} className="max-w-xl mx-auto rounded-lg min-h-[300px]" style={{
        fontFamily: theme.fontFamily,
        background: theme.backgroundType === 'gradient' ? `linear-gradient(${theme.gradientDirection || 'to bottom right'}, ${theme.gradientFrom || '#667eea'}, ${theme.gradientTo || '#764ba2'})` : theme.backgroundColor || '#ffffff',
        boxShadow: theme.containerShadow === 'lg' ? '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.1)' : theme.containerShadow === 'md' ? '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)' : theme.containerShadow === 'sm' ? '0 1px 2px 0 rgba(0,0,0,0.05)' : '0 1px 3px 0 rgba(0,0,0,0.1)',
        border: theme.containerBorder ? `1px solid ${theme.inputBorderColor || '#e5e7eb'}` : '1px solid #e5e7eb',
        borderRadius: (theme.borderRadius || 6) + 4
      }} data-id="element-403">
          <div style={{
          padding: theme.formPadding || 32
        }} data-id="element-404">
            {blocks.length === 0 && <div onDragOver={e => {
            e.preventDefault();
            setDragOverIdx(0);
          }} onDragLeave={handleDragLeave} onDrop={handleCanvasDropEnd} className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors ${dragOverIdx === 0 ? 'border-blue-400 bg-blue-50' : 'border-gray-300'}`} data-id="element-405">
                <p className="text-sm text-gray-400" data-id="element-406">
                  Drag blocks here to start building your form
                </p>
              </div>}

            {canvasRows.map((row, rowIdx) => {
            // Get the flat index of the first block in this row for the drop zone above
            const firstFlatIdx = row.blocks[0].flatIndex;
            if (row.type === 'half-row') {
              return <Fragment key={row.blocks.map(b => b.block.id).join('-')} data-id="element-407">
                    {/* Drop zone above the row */}
                    <div onDragOver={handleDragOver(firstFlatIdx)} onDragLeave={handleDragLeave} onDrop={handleDrop(firstFlatIdx)} className={`rounded transition-all ${dragOverIdx === firstFlatIdx ? 'h-3 bg-blue-400' : ''}`} style={{
                  height: dragOverIdx === firstFlatIdx ? undefined : 2
                }} data-id="element-408" />
                    {/* Two-column grid row */}
                    <div className="grid grid-cols-2 gap-3" style={{
                  marginBottom: (theme.fieldSpacing || 20) - 16
                }} data-id="element-409">
                      {row.blocks.map(item => renderBlockCard(item.block, item.flatIndex))}
                      {/* If only one half-width block, show empty slot */}
                      {row.blocks.length === 1 && <div onDragOver={e => {
                    e.preventDefault();
                    setDragOverIdx(row.blocks[0].flatIndex + 1);
                  }} onDragLeave={handleDragLeave} onDrop={handleDrop(row.blocks[0].flatIndex + 1)} className={`border-2 border-dashed rounded-md p-6 flex items-center justify-center text-xs text-gray-400 transition-colors ${dragOverIdx === row.blocks[0].flatIndex + 1 ? 'border-blue-400 bg-blue-50' : 'border-gray-200'}`} data-id="element-410">
                          Drop a half-width field here
                        </div>}
                    </div>
                  </Fragment>;
            }
            // Full-width block
            const item = row.blocks[0];
            return <Fragment key={item.block.id} data-id="element-411">
                  <div onDragOver={handleDragOver(item.flatIndex)} onDragLeave={handleDragLeave} onDrop={handleDrop(item.flatIndex)} className={`rounded transition-all ${dragOverIdx === item.flatIndex ? 'h-3 bg-blue-400' : ''}`} style={{
                height: dragOverIdx === item.flatIndex ? undefined : 2
              }} data-id="element-412" />
                  <div style={{
                marginBottom: (theme.fieldSpacing || 20) - 16
              }} data-id="element-413">
                    {renderBlockCard(item.block, item.flatIndex)}
                  </div>
                </Fragment>;
          })}

            {blocks.length > 0 && <div onDragOver={e => {
            e.preventDefault();
            setDragOverIdx(blocks.length);
          }} onDragLeave={handleDragLeave} onDrop={handleDrop(blocks.length)} className={`h-4 rounded transition-all ${dragOverIdx === blocks.length ? 'h-3 bg-blue-400' : ''}`} data-id="element-414" />}
          </div>
        </div>
      </div>

      {/* Right Panel — Properties */}
      <div className="w-72 flex-shrink-0 border-l border-gray-200 bg-white overflow-y-auto" data-id="element-415">
        {selectedBlock ? <BlockProperties block={selectedBlock} onChange={updates => updateBlock(selectedBlock.id, updates)} onBlur={commitToHistory} onDelete={() => deleteBlock(selectedBlock.id)} data-id="element-416" /> : <ThemeProperties theme={theme} onChange={setTheme} data-id="element-417" />}
      </div>
    </div>;
}
// ── Canvas Block Preview ──
function CanvasBlockPreview({
  block,
  theme
}: {
  block: FormBlock;
  theme: FormTheme;
}) {
  switch (block.type) {
    case 'heading':
      const Tag = `h${block.level || 2}` as keyof JSX.IntrinsicElements;
      return <Tag className="font-semibold" style={{
        fontSize: block.level === 1 ? 24 : block.level === 3 ? 16 : 20,
        color: theme.headingColor
      }} data-id="element-418">
          {block.content || 'Heading'}
        </Tag>;
    case 'paragraph':
      return <p className="text-sm" style={{
        color: theme.paragraphColor
      }} data-id="element-419">
          {block.content || 'Paragraph text'}
        </p>;
    case 'divider':
      return <hr className="border-gray-200" data-id="element-420" />;
    case 'spacer':
      return <div className="flex items-center justify-center text-xs text-gray-400" style={{
        height: block.height || 24
      }} data-id="element-421">
          ↕ {block.height || 24}px
        </div>;
    case 'image':
      return block.src ? <img src={block.src} alt={block.alt} className="max-w-full rounded" data-id="element-422" /> : <div className="bg-gray-100 border-2 border-dashed border-gray-300 rounded p-6 text-center text-sm text-gray-400" data-id="element-423">
          Image — set URL in properties
        </div>;
    case 'submit':
      return <div style={{
        textAlign: block.buttonAlign === 'center' ? 'center' : block.buttonAlign === 'right' ? 'right' : 'left'
      }} data-id="element-424">
          <button className={`px-5 py-2.5 text-sm font-semibold rounded-md ${block.buttonAlign === 'full' ? 'w-full' : ''}`} style={{
          backgroundColor: theme.buttonStyle === 'outline' ? 'transparent' : block.buttonColor || theme.primaryColor,
          color: theme.buttonStyle === 'outline' ? block.buttonColor || theme.primaryColor : theme.buttonTextColor,
          border: theme.buttonStyle === 'outline' ? `2px solid ${block.buttonColor || theme.primaryColor}` : 'none',
          borderRadius: theme.borderRadius,
          boxShadow: theme.buttonShadow ? '0 4px 14px 0 rgba(0,0,0,0.15)' : 'none'
        }} data-id="element-425">
            {block.buttonText || 'Submit'}
          </button>
        </div>;
    case 'consent':
      return <div data-id="element-426">
          <label className="flex items-start gap-2 cursor-pointer" data-id="element-427">
            <input type="checkbox" disabled className="mt-0.5" style={{
            accentColor: theme.primaryColor
          }} data-id="element-428" />
            <span className="text-sm" style={{
            color: theme.labelColor,
            lineHeight: 1.4
          }} data-id="element-429">
              {block.consentText || 'I agree to receive updates'}
              {block.required && <span className="text-red-500 ml-0.5" data-id="element-430">*</span>}
            </span>
          </label>
          {block.helpText && <p className="text-xs text-gray-400 mt-1 ml-6" data-id="element-431">{block.helpText}</p>}
        </div>;
    case 'hidden':
      return <div className="text-xs text-gray-400 bg-gray-50 px-2 py-1 rounded font-mono" data-id="element-432">
          hidden: {block.name} = {block.defaultValue || '(empty)'}
        </div>;
    default:
      return <div data-id="element-433">
          <div className="flex items-center gap-1 mb-1" data-id="element-434">
            <label className="block" style={{
            fontSize: theme.labelFontSize,
            fontWeight: Number(theme.labelFontWeight),
            color: theme.labelColor
          }} data-id="element-435">
              {block.label}
              {block.required && <span className="text-red-500 ml-0.5" data-id="element-436">*</span>}
            </label>
            {block.width === 'half' && <span className="text-[10px] px-1 py-0.5 bg-blue-50 text-blue-600 rounded font-medium" data-id="element-437">
                ½
              </span>}
          </div>
          {block.type === 'textarea' ? <div className="w-full h-20 px-3 py-2 text-sm text-gray-400" style={{
          borderRadius: theme.borderRadius,
          border: `1px solid ${theme.inputBorderColor}`,
          background: theme.inputBackground,
          boxShadow: theme.inputShadow ? '0 1px 3px 0 rgba(0,0,0,0.08)' : 'none'
        }} data-id="element-438">
              {block.placeholder}
            </div> : block.type === 'select' ? <div className="w-full px-3 py-2 text-sm text-gray-400" style={{
          borderRadius: theme.borderRadius,
          border: `1px solid ${theme.inputBorderColor}`,
          background: theme.inputBackground
        }} data-id="element-439">
              {block.placeholder || 'Select...'}
            </div> : block.type === 'radio' || block.type === 'checkbox' ? <div className="space-y-1 mt-1" data-id="element-440">
              {(block.validation.options || []).map((opt, i) => <label key={i} className="flex items-center gap-2 text-sm text-gray-600" data-id="element-441">
                  <input type={block.type === 'radio' ? 'radio' : 'checkbox'} disabled className="accent-blue-600" style={{
              accentColor: theme.primaryColor
            }} data-id="element-442" />
                  {opt.label}
                </label>)}
            </div> : <div className="w-full px-3 py-2 text-sm text-gray-400" style={{
          borderRadius: theme.borderRadius,
          border: `1px solid ${theme.inputBorderColor}`,
          background: theme.inputBackground,
          boxShadow: theme.inputShadow ? '0 1px 3px 0 rgba(0,0,0,0.08)' : 'none'
        }} data-id="element-443">
              {block.placeholder || block.type}
            </div>}
          {block.helpText && <p className="text-xs text-gray-400 mt-1" data-id="element-444">{block.helpText}</p>}
        </div>;
  }
}
// ── Block Properties Panel ──
function BlockProperties({
  block,
  onChange,
  onBlur,
  onDelete
}: {
  block: FormBlock;
  onChange: (updates: Partial<FormBlock>) => void;
  onBlur: () => void;
  onDelete: () => void;
}) {
  const isInput = !['heading', 'paragraph', 'divider', 'spacer', 'image', 'submit', 'hidden', 'consent'].includes(block.type);
  const hasOptions = ['select', 'radio', 'checkbox'].includes(block.type);
  return <div className="p-4 space-y-4" data-id="element-445">
      <div className="flex items-center justify-between pb-3 border-b border-gray-200" data-id="element-446">
        <h3 className="text-sm font-semibold text-gray-900 capitalize" data-id="element-447">
          {block.type === 'consent' ? 'Consent / Opt-in' : block.type.replace('_', ' ')}{' '}
          Properties
        </h3>
        <button onClick={onDelete} className="p-1 text-red-500 hover:bg-red-50 rounded" title="Delete block" data-id="element-448">
          <Trash2Icon className="h-4 w-4" data-id="element-449" />
        </button>
      </div>

      {(block.type === 'heading' || block.type === 'paragraph') && <>
          <PropField label="Content" data-id="element-450">
            <textarea value={block.content || ''} onChange={e => onChange({
          content: e.target.value
        })} onBlur={onBlur} rows={3} className="prop-input" data-id="element-451" />
          </PropField>
          {block.type === 'heading' && <PropField label="Level" data-id="element-452">
              <select value={block.level || 2} onChange={e => {
          onChange({
            level: Number(e.target.value) as 1 | 2 | 3 | 4
          });
          onBlur();
        }} className="prop-input" data-id="element-453">
                <option value={1} data-id="element-454">H1</option>
                <option value={2} data-id="element-455">H2</option>
                <option value={3} data-id="element-456">H3</option>
                <option value={4} data-id="element-457">H4</option>
              </select>
            </PropField>}
        </>}

      {block.type === 'spacer' && <PropField label="Height (px)" data-id="element-458">
          <input type="number" value={block.height || 24} onChange={e => onChange({
        height: Number(e.target.value)
      })} onBlur={onBlur} min={4} max={200} className="prop-input" data-id="element-459" />
        </PropField>}

      {block.type === 'image' && <>
          <PropField label="Image URL" data-id="element-460">
            <input type="url" value={block.src || ''} onChange={e => onChange({
          src: e.target.value
        })} onBlur={onBlur} placeholder="https://..." className="prop-input" data-id="element-461" />
          </PropField>
          <PropField label="Alt Text" data-id="element-462">
            <input type="text" value={block.alt || ''} onChange={e => onChange({
          alt: e.target.value
        })} onBlur={onBlur} className="prop-input" data-id="element-463" />
          </PropField>
        </>}

      {block.type === 'submit' && <>
          <PropField label="Button Text" data-id="element-464">
            <input type="text" value={block.buttonText || ''} onChange={e => onChange({
          buttonText: e.target.value
        })} onBlur={onBlur} className="prop-input" data-id="element-465" />
          </PropField>
          <PropField label="Button Color" data-id="element-466">
            <ColorInput value={block.buttonColor || ''} onChange={v => onChange({
          buttonColor: v
        })} data-id="element-467" />
            <p className="text-[10px] text-gray-400 mt-1" data-id="element-468">
              Leave empty to use theme primary color
            </p>
          </PropField>
          <PropField label="Alignment" data-id="element-469">
            <select value={block.buttonAlign || 'full'} onChange={e => {
          onChange({
            buttonAlign: e.target.value as any
          });
          onBlur();
        }} className="prop-input" data-id="element-470">
              <option value="left" data-id="element-471">Left</option>
              <option value="center" data-id="element-472">Center</option>
              <option value="right" data-id="element-473">Right</option>
              <option value="full" data-id="element-474">Full Width</option>
            </select>
          </PropField>
        </>}

      {block.type === 'consent' && <>
          <PropField label="Consent Text" data-id="element-475">
            <textarea value={block.consentText || ''} onChange={e => onChange({
          consentText: e.target.value
        })} onBlur={onBlur} rows={3} className="prop-input" placeholder="I agree to receive updates and communications" data-id="element-476" />
          </PropField>
          <PropField label="Field Name" data-id="element-477">
            <input type="text" value={block.name} onChange={e => onChange({
          name: e.target.value
        })} onBlur={onBlur} className="prop-input font-mono text-xs" data-id="element-478" />
          </PropField>
          <div className="flex items-center justify-between" data-id="element-479">
            <span className="text-xs font-medium text-gray-700" data-id="element-480">Required</span>
            <button onClick={() => {
          onChange({
            required: !block.required
          });
          onBlur();
        }} className={`relative w-9 h-5 rounded-full transition-colors ${block.required ? 'bg-blue-600' : 'bg-gray-300'}`} data-id="element-481">
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${block.required ? 'translate-x-4' : ''}`} data-id="element-482" />
            </button>
          </div>
          <PropField label="Help Text" data-id="element-483">
            <input type="text" value={block.helpText} onChange={e => onChange({
          helpText: e.target.value
        })} onBlur={onBlur} className="prop-input" data-id="element-484" />
          </PropField>
        </>}

      {block.type === 'hidden' && <>
          <PropField label="Field Name" data-id="element-485">
            <input type="text" value={block.name} onChange={e => onChange({
          name: e.target.value
        })} onBlur={onBlur} className="prop-input font-mono" data-id="element-486" />
          </PropField>
          <PropField label="Value" data-id="element-487">
            <input type="text" value={block.defaultValue} onChange={e => onChange({
          defaultValue: e.target.value
        })} onBlur={onBlur} className="prop-input" data-id="element-488" />
          </PropField>
        </>}

      {isInput && <>
          <PropField label="Label" data-id="element-489">
            <input type="text" value={block.label} onChange={e => onChange({
          label: e.target.value,
          name: labelToName(e.target.value) + '_' + block.id.slice(0, 4)
        })} onBlur={onBlur} className="prop-input" data-id="element-490" />
          </PropField>
          <PropField label="Field Name" data-id="element-491">
            <input type="text" value={block.name} onChange={e => onChange({
          name: e.target.value
        })} onBlur={onBlur} className="prop-input font-mono text-xs" data-id="element-492" />
          </PropField>
          {block.type !== 'radio' && block.type !== 'checkbox' && <PropField label="Placeholder" data-id="element-493">
              <input type="text" value={block.placeholder} onChange={e => onChange({
          placeholder: e.target.value
        })} onBlur={onBlur} className="prop-input" data-id="element-494" />
            </PropField>}
          <PropField label="Help Text" data-id="element-495">
            <input type="text" value={block.helpText} onChange={e => onChange({
          helpText: e.target.value
        })} onBlur={onBlur} className="prop-input" data-id="element-496" />
          </PropField>
          <div className="flex items-center justify-between" data-id="element-497">
            <span className="text-xs font-medium text-gray-700" data-id="element-498">Required</span>
            <button onClick={() => {
          onChange({
            required: !block.required
          });
          onBlur();
        }} className={`relative w-9 h-5 rounded-full transition-colors ${block.required ? 'bg-blue-600' : 'bg-gray-300'}`} data-id="element-499">
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${block.required ? 'translate-x-4' : ''}`} data-id="element-500" />
            </button>
          </div>
          <PropField label="Width" data-id="element-501">
            <select value={block.width} onChange={e => {
          onChange({
            width: e.target.value as 'full' | 'half'
          });
          onBlur();
        }} className="prop-input" data-id="element-502">
              <option value="full" data-id="element-503">Full Width</option>
              <option value="half" data-id="element-504">Half Width (side by side)</option>
            </select>
          </PropField>
        </>}

      {hasOptions && <div className="space-y-2" data-id="element-505">
          <div className="flex items-center justify-between" data-id="element-506">
            <span className="text-xs font-medium text-gray-700" data-id="element-507">Options</span>
            <button onClick={() => {
          const opts = [...(block.validation.options || [])];
          opts.push({
            label: `Option ${opts.length + 1}`,
            value: `option_${opts.length + 1}`
          });
          onChange({
            validation: {
              ...block.validation,
              options: opts
            }
          });
          onBlur();
        }} className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-0.5" data-id="element-508">
              <PlusIcon className="h-3 w-3" data-id="element-509" /> Add
            </button>
          </div>
          {(block.validation.options || []).map((opt, i) => <div key={i} className="flex items-center gap-1" data-id="element-510">
              <input type="text" value={opt.label} onChange={e => {
          const opts = [...(block.validation.options || [])];
          opts[i] = {
            ...opts[i],
            label: e.target.value,
            value: labelToName(e.target.value)
          };
          onChange({
            validation: {
              ...block.validation,
              options: opts
            }
          });
        }} onBlur={onBlur} className="prop-input flex-1 text-xs" placeholder="Label" data-id="element-511" />
              <button onClick={() => {
          const opts = (block.validation.options || []).filter((_, j) => j !== i);
          onChange({
            validation: {
              ...block.validation,
              options: opts
            }
          });
          onBlur();
        }} className="p-1 text-gray-400 hover:text-red-500" data-id="element-512">
                <XIcon className="h-3 w-3" data-id="element-513" />
              </button>
            </div>)}
        </div>}
    </div>;
}
// ── Theme Properties Panel ──
function ThemeProperties({
  theme,
  onChange
}: {
  theme: FormTheme;
  onChange: (t: FormTheme) => void;
}) {
  const [activeSection, setActiveSection] = useState<string>('presets');
  const applyPreset = (preset: StylePreset) => {
    onChange({
      ...theme,
      ...preset.theme
    });
  };
  const sections = [{
    id: 'presets',
    label: 'Style Presets',
    icon: SwatchBookIcon
  }, {
    id: 'colors',
    label: 'Colors & Background',
    icon: PaletteIcon
  }, {
    id: 'typography',
    label: 'Typography',
    icon: TypeIcon
  }, {
    id: 'inputs',
    label: 'Input Fields',
    icon: AlignLeftIcon
  }, {
    id: 'button',
    label: 'Button',
    icon: SendIcon
  }, {
    id: 'layout',
    label: 'Layout & Spacing',
    icon: MoveVerticalIcon
  }];
  return <div className="divide-y divide-gray-100" data-id="element-514">
      <div className="p-4 pb-3" data-id="element-515">
        <div className="flex items-center gap-2" data-id="element-516">
          <PaletteIcon className="h-4 w-4 text-gray-400" data-id="element-517" />
          <h3 className="text-sm font-semibold text-gray-900" data-id="element-518">Form Theme</h3>
        </div>
        <p className="text-xs text-gray-500 mt-1" data-id="element-519">
          Click a block to edit its properties
        </p>
      </div>

      <div className="px-2 py-2 flex flex-wrap gap-1" data-id="element-520">
        {sections.map(s => {
        const Icon = s.icon;
        return <button key={s.id} onClick={() => setActiveSection(activeSection === s.id ? '' : s.id)} className={`flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-md transition-colors ${activeSection === s.id ? 'bg-gray-900 text-white' : 'text-gray-600 bg-gray-50 hover:bg-gray-100'}`} data-id="element-521">
              <Icon className="h-3 w-3" data-id="element-522" />
              {s.label}
            </button>;
      })}
      </div>

      {activeSection === 'presets' && <div className="p-4 space-y-3" data-id="element-523">
          <p className="text-xs text-gray-500" data-id="element-524">
            Click a preset to instantly apply a complete style.
          </p>
          <div className="grid grid-cols-2 gap-2" data-id="element-525">
            {FORM_STYLE_PRESETS.map(preset => <button key={preset.name} onClick={() => applyPreset(preset)} className="flex flex-col items-center gap-1.5 p-3 rounded-lg border border-gray-200 hover:border-blue-300 hover:shadow-sm transition-all text-center group" data-id="element-526">
                <span className="text-lg" data-id="element-527">{preset.emoji}</span>
                <span className="text-[11px] font-medium text-gray-700 group-hover:text-blue-700" data-id="element-528">
                  {preset.name}
                </span>
                <div className="flex gap-0.5" data-id="element-529">
                  <div className="w-3 h-3 rounded-full border border-gray-200" style={{
              background: preset.theme.primaryColor || theme.primaryColor
            }} data-id="element-530" />
                  <div className="w-3 h-3 rounded-full border border-gray-200" style={{
              background: preset.theme.backgroundColor || theme.backgroundColor
            }} data-id="element-531" />
                  <div className="w-3 h-3 rounded-full border border-gray-200" style={{
              background: preset.theme.labelColor || theme.labelColor
            }} data-id="element-532" />
                </div>
              </button>)}
          </div>
        </div>}

      {activeSection === 'colors' && <div className="p-4 space-y-4" data-id="element-533">
          <PropField label="Primary Color" data-id="element-534">
            <ColorInput value={theme.primaryColor} onChange={v => onChange({
          ...theme,
          primaryColor: v
        })} data-id="element-535" />
          </PropField>
          <PropField label="Background Type" data-id="element-536">
            <select value={theme.backgroundType} onChange={e => onChange({
          ...theme,
          backgroundType: e.target.value as 'solid' | 'gradient'
        })} className="prop-input" data-id="element-537">
              <option value="solid" data-id="element-538">Solid Color</option>
              <option value="gradient" data-id="element-539">Gradient</option>
            </select>
          </PropField>
          {theme.backgroundType === 'solid' ? <PropField label="Background Color" data-id="element-540">
              <ColorInput value={theme.backgroundColor} onChange={v => onChange({
          ...theme,
          backgroundColor: v
        })} data-id="element-541" />
            </PropField> : <>
              <PropField label="Gradient From" data-id="element-542">
                <ColorInput value={theme.gradientFrom} onChange={v => onChange({
            ...theme,
            gradientFrom: v
          })} data-id="element-543" />
              </PropField>
              <PropField label="Gradient To" data-id="element-544">
                <ColorInput value={theme.gradientTo} onChange={v => onChange({
            ...theme,
            gradientTo: v
          })} data-id="element-545" />
              </PropField>
              <PropField label="Direction" data-id="element-546">
                <select value={theme.gradientDirection} onChange={e => onChange({
            ...theme,
            gradientDirection: e.target.value as any
          })} className="prop-input" data-id="element-547">
                  <option value="to bottom" data-id="element-548">Top → Bottom</option>
                  <option value="to right" data-id="element-549">Left → Right</option>
                  <option value="to bottom right" data-id="element-550">Diagonal ↘</option>
                  <option value="to top right" data-id="element-551">Diagonal ↗</option>
                </select>
              </PropField>
            </>}
          <PropField label="Heading Color" data-id="element-552">
            <ColorInput value={theme.headingColor} onChange={v => onChange({
          ...theme,
          headingColor: v
        })} data-id="element-553" />
          </PropField>
          <PropField label="Paragraph Color" data-id="element-554">
            <ColorInput value={theme.paragraphColor} onChange={v => onChange({
          ...theme,
          paragraphColor: v
        })} data-id="element-555" />
          </PropField>
        </div>}

      {activeSection === 'typography' && <div className="p-4 space-y-4" data-id="element-556">
          <PropField label="Font Family" data-id="element-557">
            <select value={theme.fontFamily} onChange={e => onChange({
          ...theme,
          fontFamily: e.target.value
        })} className="prop-input" data-id="element-558">
              <option value="Inter, system-ui, sans-serif" data-id="element-559">Inter</option>
              <option value="Arial, Helvetica, sans-serif" data-id="element-560">Arial</option>
              <option value="Georgia, serif" data-id="element-561">Georgia</option>
              <option value="'Courier New', monospace" data-id="element-562">Courier New</option>
              <option value="'Segoe UI', system-ui, sans-serif" data-id="element-563">
                Segoe UI
              </option>
              <option value="'Trebuchet MS', sans-serif" data-id="element-564">Trebuchet MS</option>
              <option value="Verdana, sans-serif" data-id="element-565">Verdana</option>
              <option value="'Times New Roman', serif" data-id="element-566">Times New Roman</option>
              <option value="Tahoma, sans-serif" data-id="element-567">Tahoma</option>
              <option value="'Palatino Linotype', serif" data-id="element-568">Palatino</option>
            </select>
          </PropField>
          <PropField label="Label Color" data-id="element-569">
            <ColorInput value={theme.labelColor} onChange={v => onChange({
          ...theme,
          labelColor: v
        })} data-id="element-570" />
          </PropField>
          <PropField label="Label Font Size" data-id="element-571">
            <div className="flex items-center gap-2" data-id="element-572">
              <input type="range" min={11} max={18} value={theme.labelFontSize} onChange={e => onChange({
            ...theme,
            labelFontSize: Number(e.target.value)
          })} className="flex-1" data-id="element-573" />
              <span className="text-xs text-gray-500 w-8 text-right" data-id="element-574">
                {theme.labelFontSize}px
              </span>
            </div>
          </PropField>
          <PropField label="Label Weight" data-id="element-575">
            <select value={theme.labelFontWeight} onChange={e => onChange({
          ...theme,
          labelFontWeight: e.target.value as any
        })} className="prop-input" data-id="element-576">
              <option value="400" data-id="element-577">Normal (400)</option>
              <option value="500" data-id="element-578">Medium (500)</option>
              <option value="600" data-id="element-579">Semibold (600)</option>
              <option value="700" data-id="element-580">Bold (700)</option>
            </select>
          </PropField>
        </div>}

      {activeSection === 'inputs' && <div className="p-4 space-y-4" data-id="element-581">
          <PropField label="Border Color" data-id="element-582">
            <ColorInput value={theme.inputBorderColor} onChange={v => onChange({
          ...theme,
          inputBorderColor: v
        })} data-id="element-583" />
          </PropField>
          <PropField label="Input Background" data-id="element-584">
            <ColorInput value={theme.inputBackground} onChange={v => onChange({
          ...theme,
          inputBackground: v
        })} data-id="element-585" />
          </PropField>
          <PropField label="Focus Ring Color" data-id="element-586">
            <ColorInput value={theme.inputFocusColor} onChange={v => onChange({
          ...theme,
          inputFocusColor: v
        })} data-id="element-587" />
          </PropField>
          <PropField label="Border Radius" data-id="element-588">
            <div className="flex items-center gap-2" data-id="element-589">
              <input type="range" min={0} max={20} value={theme.borderRadius} onChange={e => onChange({
            ...theme,
            borderRadius: Number(e.target.value)
          })} className="flex-1" data-id="element-590" />
              <span className="text-xs text-gray-500 w-8 text-right" data-id="element-591">
                {theme.borderRadius}px
              </span>
            </div>
          </PropField>
          <ToggleField label="Input Shadow" value={theme.inputShadow} onChange={v => onChange({
        ...theme,
        inputShadow: v
      })} data-id="element-592" />
        </div>}

      {activeSection === 'button' && <div className="p-4 space-y-4" data-id="element-593">
          <PropField label="Button Style" data-id="element-594">
            <select value={theme.buttonStyle} onChange={e => onChange({
          ...theme,
          buttonStyle: e.target.value as 'filled' | 'outline'
        })} className="prop-input" data-id="element-595">
              <option value="filled" data-id="element-596">Filled</option>
              <option value="outline" data-id="element-597">Outline</option>
            </select>
          </PropField>
          <PropField label="Button Text Color" data-id="element-598">
            <ColorInput value={theme.buttonTextColor} onChange={v => onChange({
          ...theme,
          buttonTextColor: v
        })} data-id="element-599" />
          </PropField>
          <ToggleField label="Button Shadow" value={theme.buttonShadow} onChange={v => onChange({
        ...theme,
        buttonShadow: v
      })} data-id="element-600" />
        </div>}

      {activeSection === 'layout' && <div className="p-4 space-y-4" data-id="element-601">
          <PropField label="Form Padding" data-id="element-602">
            <div className="flex items-center gap-2" data-id="element-603">
              <input type="range" min={16} max={64} value={theme.formPadding} onChange={e => onChange({
            ...theme,
            formPadding: Number(e.target.value)
          })} className="flex-1" data-id="element-604" />
              <span className="text-xs text-gray-500 w-8 text-right" data-id="element-605">
                {theme.formPadding}px
              </span>
            </div>
          </PropField>
          <PropField label="Field Spacing" data-id="element-606">
            <div className="flex items-center gap-2" data-id="element-607">
              <input type="range" min={8} max={40} value={theme.fieldSpacing} onChange={e => onChange({
            ...theme,
            fieldSpacing: Number(e.target.value)
          })} className="flex-1" data-id="element-608" />
              <span className="text-xs text-gray-500 w-8 text-right" data-id="element-609">
                {theme.fieldSpacing}px
              </span>
            </div>
          </PropField>
          <PropField label="Container Shadow" data-id="element-610">
            <select value={theme.containerShadow} onChange={e => onChange({
          ...theme,
          containerShadow: e.target.value as any
        })} className="prop-input" data-id="element-611">
              <option value="none" data-id="element-612">None</option>
              <option value="sm" data-id="element-613">Small</option>
              <option value="md" data-id="element-614">Medium</option>
              <option value="lg" data-id="element-615">Large</option>
            </select>
          </PropField>
          <ToggleField label="Container Border" value={theme.containerBorder} onChange={v => onChange({
        ...theme,
        containerBorder: v
      })} data-id="element-616" />
        </div>}
    </div>;
}
// ── Shared UI Components ──
function PropField({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}) {
  return <div data-id="element-617">
      <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-618">
        {label}
      </label>
      {children}
    </div>;
}
function ColorInput({
  value,
  onChange
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const hexValue = value && value.startsWith('#') && value.length <= 9 ? value : '#000000';
  return <div className="flex items-center gap-2" data-id="element-619">
      <input type="color" value={hexValue} onChange={e => onChange(e.target.value)} className="h-8 w-8 rounded border border-gray-300 cursor-pointer flex-shrink-0" data-id="element-620" />
      <input type="text" value={value} onChange={e => onChange(e.target.value)} className="prop-input flex-1 font-mono text-xs" data-id="element-621" />
    </div>;
}
function ToggleField({
  label,
  value,
  onChange
}: {
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return <div className="flex items-center justify-between" data-id="element-622">
      <span className="text-xs font-medium text-gray-700" data-id="element-623">{label}</span>
      <button onClick={() => onChange(!value)} className={`relative w-9 h-5 rounded-full transition-colors ${value ? 'bg-blue-600' : 'bg-gray-300'}`} data-id="element-624">
        <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${value ? 'translate-x-4' : ''}`} data-id="element-625" />
      </button>
    </div>;
}